import React, { FC } from "react";
import { OptimizelyContactPerson } from "../../types/optimizely/articlePage";
import { OptimizelyContentArea } from "../../types/optimizely/base";
import { mapContentToBlock } from "../../utils/mapContentToBlock";
import { ContentArea } from "../ContentArea/ContentArea";
import { ContactBlock, isContactBlock, isEditorialBlock } from "../../types/optimizely/blocks";
import { OptimizelyExpandedValue } from "../../types/optimizely/expanded";

interface ContactPersonProps {
  primaryContactPersonsContentArea: OptimizelyContactPerson[];
  secondaryContactPersonsContentArea?: OptimizelyContactPerson[];
  informationContentAreaRight: OptimizelyContentArea;
}

const getGridColSpan = (
  index: number,
  numberOfContactBlocks: number
): string => {
  if (numberOfContactBlocks % 2 === 0) {
    return "col-span-2 lg:col-span-1";
  }

  if (index === numberOfContactBlocks - 1) {
    return "col-span-2";
  } else {
    return "col-span-2 lg:col-span-1";
  }
};

const mapOptimizelyContactPersonToContactPerson = (
  contactPerson: OptimizelyContactPerson | ContactBlock,
  classOverride: string = "col-span-2 lg:col-span-1"
) => {
  if (!contactPerson) {
    return null;
  }

  if (isContactBlock(contactPerson)) {
    return (
      <div className="py-10 px-5 md:px-16 bg-[#E5E5E5]">
        {mapContentToBlock(contactPerson)}
      </div>
    );
  }

  if (contactPerson.contentType[1] !== "PersonBlock") {
    return null;
  }

  const {
    firstName: { value: firstName },
    lastName: { value: lastName },
    mobileNumber: { value: mobileNumber },
    phoneNumber: { value: phoneNumber },
    mainImage: { expandedValue: mainImage },
    workTitle: { value: workTitle },
    email: { value: email },
    organization: { value: organizations },
    address: { value: address },
  } = contactPerson;

  return (
    <div
      className={`py-10 px-5 md:px-16 flex flex-col sm:flex-row bg-[#E5E5E5] ${classOverride}`}
    >
      {mainImage?.url ? (
        <img
          loading="lazy"
          decoding="async"
          src={mainImage?.url}
          className="h-[150px] w-fit mb-1 sm:mb-0 ml-4 sm:ml-0"
        />
      ) : null}
      <div className="ml-4 w-[250px] text-[14px] text-primary break-words">
        <p className="text-[16px] font-bold mb-2 break-words">
          {firstName} {lastName}
        </p>
        <p className="break-words">{workTitle}</p>
        <p className="break-words italic">
          {organizations
            .map(function (v) {
              return v.name;
            })
            .join(", ")}
        </p>
        {(mobileNumber || phoneNumber) && (
          <p className="break-words">
            Tel: {[mobileNumber, phoneNumber].filter(Boolean).join(", ")}
          </p>
        )}
        <a
          className="text-primaryBlue underline break-words"
          href={`mailto:${email}`}
        >
          {email}
        </a>
        {address && <p className="break-words">Address: {address}</p>}
      </div>
    </div>
  );
};

const ContactPerson: FC<ContactPersonProps> = (props: ContactPersonProps) => {
  const {
    primaryContactPersonsContentArea,
    secondaryContactPersonsContentArea,
    informationContentAreaRight,
  } = props;

  const splitInformationContentArea = (): OptimizelyContentArea[] => {
    if (!informationContentAreaRight?.expandedValue) return [];

    const { expandedValue, propertyDataType } = informationContentAreaRight;

    return expandedValue.map((module: OptimizelyExpandedValue) => ({
      propertyDataType,
      expandedValue: [module],
      contentType: module.contentType,
      value: [{ displayOption: "", contentLink: module.contentLink }],
    }));
  };

  const items: (
    | OptimizelyContactPerson
    | OptimizelyContentArea
  )[] = [
    ...(primaryContactPersonsContentArea || []),
    ...(secondaryContactPersonsContentArea || []),
    ...splitInformationContentArea(),
  ];

  const lastIndexOfContactBlock =
    (primaryContactPersonsContentArea?.length || 0) -
    1 +
    (secondaryContactPersonsContentArea?.length || 0);

  const isContactPersonBlock = (
    item: OptimizelyContactPerson | OptimizelyContentArea,
    index: number
  ) =>
    index <= lastIndexOfContactBlock &&
    !isEditorialBlock(item as OptimizelyExpandedValue);

  return (
    <div className="max-w-[1320px] mx-auto">
      <div
        className={`mx-auto grid grid-cols-1 lg:grid-cols-2 gap-4 divide-neutralYellow`}
      >
        {/* <div
        className={`mx-auto grid grid-cols-1 lg:grid-cols-2 gap-4 [&>*]:col-span-1 [&>*]:w-full [&>*:only-child]:col-span-2 [&>*:last-child:nth-child(odd)]:col-span-2 divide-neutralYellow`}
      > */}
        {items?.map(
          (item: OptimizelyContactPerson | OptimizelyContentArea, index) => {
            if (isContactPersonBlock(item, index)) {
              return mapOptimizelyContactPersonToContactPerson(
                item as OptimizelyContactPerson,
                getGridColSpan(index, items.length)
              );
            } else {
              return (
                <div
                  className={`bg-secondaryGreen ${getGridColSpan(
                    index,
                    items.length
                  )}`}
                  key={'contact-person-' + index}
                >
                  <div className="h-full mx-auto py-10 px-16">
                    <ContentArea contentArea={item as OptimizelyContentArea} />
                  </div>
                </div>
              );
            }
          }
        )}
      </div>
    </div>
  );
};

export default ContactPerson;
